import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { Popup } from "@progress/kendo-react-popup";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { setCloneTaskDetails } from "../../../actions/cloneTaskDetails";
import { resetPlanMyday } from "../../../actions/planMyDay";
import { setShowTaskDetailPanel } from "../../../actions/sidebarContent";
import { resetRecurrence, setExpanded, setShowTaskSidebar, setTaskOnBlur, storeTaskUrl, toggleApproval, setExportTask } from "../../../actions/taskSidebar";
import { action, button, icon, label, notifyIcon, number, quote, route } from "../../../config";
import config from "../../../env.config";
import { generateYdocRoomId, getMaxSortId, getQueryParams, getTaskUrl, popupAlign } from "../../../helper/common";
import { usePopup } from "../../../helper/commonHooks";
import {
  trackAcceptTask,
  trackArchiveTask,
  trackCloneTask,
  trackReopenTask,
  trackRejectTask,
  trackRetractTask,
} from "../../../utils/analytics";
import { getNotification, isTrueBit, sanitizeUrl, splitString } from "../../../utils/common";
import { updateBookmark } from "../../Bookmarks/bookmarks.service";
import { handleCloseSidebar } from "../../Tasks/tasks.common";
import { getTasks, handleTaskKeyUpdate, closeTask } from "../../Tasks/tasks.service";
import { updatedClonedTaskName } from "../sidebar.helper";
import CloseTaskPopup from "./CloseTaskPopup";
import DeletePopover from "./DeletePopover";
import TaskLockPopover from "./TaskLockPopover";
import { taskAction } from "./TaskSidebarHeader.helper";
import UndoNotification from "./UndoNotification";
import ModalPopUp from "../../../shared/layouts/Modal/Modal";
import { exportButtons } from "../../../shared/layouts/layout.constants";
import TaskPDFExport from "./TaskPDFExport";
import { getFormSubmission } from "../../../shared/services/workflow.services";

/**
 * HeaderActions component
 * @props {}
 */
const HeaderActions = () => {
  const history = useHistory();
  const [taskUrl, setTaskUrl] = useState("");
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showUndoDelete, setShowUndoDelete] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [clonePopup, setClonePopup] = useState(false);
  const [closePopup, setClosePopup] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [exportSections, setExportSections] = useState({
    description: true,
    approvals: false,
    forms: false,
    reviewSection: false,
    emails: false,
    attachments: false,
    activityLogs: false,
    comments: false,
    linkedTasks: false,
    tags: false,
    followers: false,
    selectAll: false
  });
  const [fileName, setFileName] = useState("");
  const [isLoadingFormData, setIsLoadingFormData] = useState(false);
  const [preloadedFormData, setPreloadedFormData] = useState(null);

  const deleteTaskRef = useRef(null);
  const cloneTaskRef = useRef(null);
  const closeTaskRef = useRef(null);
  const exportTaskRef = useRef(null);
  const closeTaskModalOpenRef = useRef(false);
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const {
    task,
    isNewTask,
    newTaskData,
    isNewTaskSaved,
    tempTaskDesc,
    showDescPopup,
    expanded,
    isApproval,
    isExistingTaskDescChanged,
    taskDescChangeLogSent,
    flowElements,
  } = useSelector((state) => state.taskSidebar);
  const { defaultDetails } = useSelector((store) => store.tasks);
  const { show, setShow, anchor, contentRef: deleteRef, blurTimeoutRef, onOpen, onFocus, onBlur } = usePopup();
  const { user } = useSelector((state) => state.auth);
  const taskSidebarData = {
    task,
    isNewTask,
    newTaskData,
    isNewTaskSaved,
    tempTaskDesc,
    showDescPopup,
    expanded,
    isApproval,
    user,
    isExistingTaskDescChanged,
    taskDescChangeLogSent,
  };
  const createTask = getQueryParams("createTask", window.location.search);
  const { show: showLockPopover, setShow: setShowLockPopover, anchor: lockPopoverAnchor } = usePopup();
  const [loading, setLoading] = useState(false),
    bookmarks = useSelector((state) => state.bookmarks),
    loggedInUser = useSelector((state) => state.auth.user),
    isTaskBookmarked = bookmarks?.taskBookmarks?.find((taskElement) => taskElement.Id === task.taskId);
  const app = useSelector((state) => state.app);
  const { allAttachments } = useSelector((state) => state.attachments);
  const { subTask } = useSelector((state) => state.linkedTasks);
  const location = useLocation();

  const projectCode = useMemo(() => {
    return defaultDetails?.allProjectsList?.find(
      (project) => project.ProjectId === (newTaskData?.initialProjectChangedFromText ? newTaskData?.initialProjectId : newTaskData?.project)
    )?.ProjectCode;
  }, [newTaskData?.project, newTaskData?.initialProjectChangedFromText, newTaskData?.initialProjectId]);

  /**
   * Show close button if the task is created by the logged in user or the task is a personal task or the logged in user is an owner or collaborator of a team/project
   * @returns {boolean}
   * @author Bhavana
   */
  const showCloseButton = useMemo(() => {
    if (!task) return false;
    const userId = loggedInUser?.id;
    if (parseInt(task.CreatedBy) === userId) return true;
    const project = defaultDetails?.allProjectsList?.find((p) => p.ProjectId === task?.CurrentProject);
    if (!project) return false;
    const owners = project.IsPersonal ? [project.user] : splitString(project.owners, ",") || [];
    const collaborators = project.IsPersonal ? [project.user] : splitString(project.collaborators, ",") || [];
    return [...owners, ...collaborators].some((id) => parseInt(id) === userId);
  }, [loggedInUser?.id, task, defaultDetails?.allProjectsList]);

  const deleteDisabled = useMemo(() => {
    if(task?.TeamMangers?.includes(Number(user?.id))) return false;
    if(Number(task?.CreatedBy) === Number(user?.id)) return false;
    return true;
  }, [task?.TeamMangers, user?.id]);

  /**
   * set approval status icon for tasksidebar
   * @author Himanshu Negi
   * */

  useEffect(() => {
    setApprovalStatus(
      defaultDetails &&
        Array.isArray(defaultDetails?.approvalStatus) &&
        defaultDetails?.approvalStatus?.find((action) => action.StatusId === task?.approvalStatus)
    );
  }, [task?.taskId, task.approvalStatus]);

  /** sets disable condition */
  useEffect(() => {
    setIsDisabled(task ? isTrueBit(task, "IsLocked") : false);
  }, [isNewTask, task.taskId, newTaskData]);

  useEffect(() => {
    (async () => {
      if (!isNewTask && task.taskId) {
        setTaskUrl(sanitizeUrl(getTaskUrl(task.taskId)));
      }
    })();
  }, [isNewTask, task.taskId]);

  /**
   * To generate and store in redux of task URL data.
   */
  useEffect(() => {
    (async () => {
      const tenantAppUrl = app.tenantAppUrl;
      const baseUrl = tenantAppUrl ? tenantAppUrl : config.BASE_URL.APP_URL;
      if (!isNewTask) {
        await dispatch(storeTaskUrl(baseUrl + sanitizeUrl(getTaskUrl(task.taskId))));
      }
    })();
  }, [!isNewTask, task.taskId]);

  const handleTaskAction = async (type) => {
    let response = await taskAction(type, auth);
    if (response && task?.taskId) {
      const tid = { task_id: task.taskId };
      if (type === "archive") trackArchiveTask(tid);
      else if (type === "clone") trackCloneTask(tid);
      else if (type === "reopen") trackReopenTask(tid);
      else if (type === "retract") trackRetractTask(tid);
      else if (type === "reject") trackRejectTask(tid);
      else if (type === "accept") trackAcceptTask(tid);
      getTasks();
      dispatch(setShowTaskSidebar({ showTaskSidebar: false }));
    }
  };

  /**
   * Click to open popover.
   *  @return {JSX Element}
   */
  const deleteTask = useCallback(() => {
    setShowConfirmDelete(true);
    setClonePopup(false);
    setClosePopup(false);
    setShowLockPopover(false);
  }, []);

  /**
   *  closes the sidebar
   * @param {void}
   * @returns {void}
   */
  const exitSidebar = async () => {
    await handleCloseSidebar(dispatch, taskSidebarData);
    if (location.pathname === route.PRIVATE_ROUTE.TASK_SIDEBAR.PATH) {
       history.push(route.PRIVATE_ROUTE.LANDING_PAGE.PATH);
      window.dispatchEvent(new CustomEvent('taskSidebarClosed'));
      return;
    }
    createTask && history.push(`/`);
    window.dispatchEvent(new CustomEvent('taskSidebarClosed'));
  };

  const onSettings = async () => {
    await setShow(!show);
    {
      showConfirmDelete && setShowConfirmDelete(!showConfirmDelete);
    }
  };

  /**
   * open the taskSidebar for cloning task ,
   * generates new task Id
   */
  const handleCloneTask = useCallback(() => {
    setClonePopup(true);
    setShowConfirmDelete(false);
    setClosePopup(false);
    setShowLockPopover(false);
  }, []);

  /**
   * Show close task confirmation popup
   * @param {void}
   * @returns {void}
   */
  const handleCloseTaskClick = useCallback(() => {
    closeTaskModalOpenRef.current = true;
    setClosePopup(true);
    setShowConfirmDelete(false);
    setClonePopup(false);
    setShowLockPopover(false);
  }, []);

  const confirmCloning = async () => {
    await handleCloseSidebar(dispatch, taskSidebarData);
    setShow(!show);
    setClonePopup(false);
    setClosePopup(false);
    setShowConfirmDelete(false);
    const cloneTask = {
      taskId: task?.taskId,
      name: updatedClonedTaskName(task?.Name),
      description: task?.Description ? task?.Description : "",
      tags: task?.tagIds,
      attachmentCount: task?.attachmentCount,
      priority: task?.Priority,
      followers: task?.followerIds,
      dueDate: task?.DueDate,
      currentProject: task?.CurrentProject,
      flowElements: [...flowElements],
      attachments: allAttachments?.map((attachment) => ({ ...attachment, IsCopyAttachment: true })),
      subTask: [...subTask],
      subtaskCount: task?.subTaskCount,
      mode: task?.mode,
    };
    dispatch(resetPlanMyday());
    dispatch(setCloneTaskDetails({ isCloneTask: true, cloneTask }));
    dispatch(setShowTaskSidebar({ isNewTask: true, showTaskSidebar: true, tempYdocRoom: generateYdocRoomId(user?.id) }));
    dispatch(resetRecurrence());
  };

  /**
   * closes the popup
   * @param {void}
   * @return {void}
   */
  const hideOnBlur = useCallback(() => {
    if (closeTaskModalOpenRef.current) {
      return;
    }
    setShow(false);
    setClonePopup(false);
    setClosePopup(false);
    setShowLockPopover(false);
  }, []);

  const handleCloseTaskModal = useCallback(() => {
    closeTaskModalOpenRef.current = false;
    setClosePopup(false);
    setShow(false);
  }, [setShow]);

  /**
   * open content sections by clicking quick action buttons
   * @param {element}
   * @returns {void}
   */
  const handleOpenWorkflowElements = async () => {
    var elmnt = document.getElementById("task-workflow");
    await dispatch(setExpanded([...expanded, "task-workflow"]));
    elmnt.scrollIntoView();
  };

  /**
   * toggle task lock confirmation popup
   * @param {void}
   * @returns {void}
   * @author Himanshu Negi
   */
  const toggleTaskLockPopOver = useCallback(() => {
    if (task?.IsTaskLocked && task?.LockedBy !== user?.id) return;
    if (task?.IsTaskLocked) {
      handleLockTask();
      return;
    }
    setShowLockPopover(!showLockPopover);
    setClonePopup(false);
    setClosePopup(false);
    setShowConfirmDelete(false);
  }, [showLockPopover, task?.IsTaskLocked, task?.LockedBy]);

  /**
   * Task lock confirmation logic
   * @param {void}
   * @returns {void}
   * @author Himanshu Negi
   */
  const handleLockTask = useCallback(async () => {
    const isTaskLocked = task?.IsTaskLocked ? number.ZERO : number.ONE;
    await handleTaskKeyUpdate(isNewTask, "isTaskLocked", isTaskLocked);
    dispatch(setTaskOnBlur({ key: "LockedBy", value: isTaskLocked ? user?.id : null }));
    if (!task?.ShowLockedDescription) {
      dispatch(setTaskOnBlur({ key: "ShowLockedDescription", value: 1 }));
      !task?.ApprovalDescription && dispatch(setTaskOnBlur({ key: "ApprovalDescription", value: task?.Description }));
      dispatch(setTaskOnBlur({ key: "Description", value: "" }));
    }
    setClonePopup(false);
    setClosePopup(false);
    setShowConfirmDelete(false);
  }, [task, isNewTask]);

  const confirmBookmarking = async () => {
    // Logic to bookmark the task
    setLoading(true);
    if (isTaskBookmarked || bookmarks.taskBookmarks.length < number.TEN) {
      let location = getMaxSortId(bookmarks.taskBookmarks, "SortId");
      const payload = { userId: loggedInUser.id, entityType: "Tasks", entityId: task.taskId, sortId: number.ZERO };
      await dispatch(updateBookmark(payload));
    } else {
      getNotification(quote.TASK_BOOKMARK_ALERT, notifyIcon.ERROR_ICON);
    }
    setLoading(false);
    setShowConfirmDelete(false);
    setClonePopup(false);
    setClosePopup(false);
    setShowLockPopover(false);
    setShow(false);
  };

  const isVisible = !isNewTask && ((isTrueBit(task, "IsLocked") && !task.IsPassedOn) || task.isFollowed);

  /**
   * Toggle approvals
   * @param { Void }
   * @author Himanshu Negi
   */
  const handleApprovalTask = useCallback(() => {
    dispatch(toggleApproval(!isApproval));
    dispatch(setShowTaskDetailPanel(false));
    hideOnBlur();
  }, []);

  /**
   * Dynamic export buttons - disable Export button when Forms is loading
   * and change text to "Loading data..."
   */
  const exportPrefetchSections = exportSections.forms || exportSections.reviewSection;

  const dynamicExportButtons = useMemo(() => {
    return exportButtons.map(button => {
      if (button.action === action.CONFIRM) {
        const isLoading = exportPrefetchSections && isLoadingFormData;
        return {
          ...button,
          label: isLoading ? "Loading data..." : button.label,
          disabled: isLoading
        };
      }
      return button;
    });
  }, [exportPrefetchSections, isLoadingFormData]);

  const handleExportTask = () => {
    const defaultFileName = task?.ProjectTaskId + ": "+ task?.Name;
    setFileName(defaultFileName);
    setShowExportModal(true);
  };

  /**
   * Pre-fetch form data when Forms checkbox is checked
   */
  useEffect(() => {
    const fetchFormDataForExport = async () => {
      if (showExportModal && exportPrefetchSections && task?.submittedFormId && !preloadedFormData) {
        setIsLoadingFormData(true);
        try {
          const submittedFormData = await getFormSubmission({
            submittedFormId: task?.submittedFormId,
          });
          if (submittedFormData) {
            setPreloadedFormData(submittedFormData);
          }
        } catch (error) {
          console.error("Error pre-fetching form data for export:", error);
          setPreloadedFormData(null);
        } finally {
          setIsLoadingFormData(false);
        }
      } else if (!exportPrefetchSections) {
        setPreloadedFormData(null);
        setIsLoadingFormData(false);
      }
    };

    fetchFormDataForExport();
  }, [showExportModal, exportPrefetchSections, task?.submittedFormId]);

  /**
   * Handle checkbox changes for export sections
   */
  const handleSectionChange = (section) => {
    if (section === 'selectAll') {
      const newValue = !exportSections.selectAll;
      // Only update enabled options when Select All is clicked
      const enabledOptions = availableOptions.filter(option => option.enabled);
      const newSections = { ...exportSections };

      enabledOptions.forEach(option => {
        newSections[option.key] = newValue;
      });
      newSections.selectAll = newValue;

      setExportSections(newSections);
    } else {
      // Only allow changes to enabled options
      const option = availableOptions.find(opt => opt.key === section);
      if (!option || !option.enabled) return;

      const newSections = {
        ...exportSections,
        [section]: !exportSections[section]
      };

      // Update selectAll based on enabled sections only
      const enabledSectionKeys = availableOptions.filter(opt => opt.enabled).map(opt => opt.key);
      const allEnabledSelected = enabledSectionKeys.every(key => newSections[key]);
      newSections.selectAll = allEnabledSelected;

      setExportSections(newSections);
    }
  };

  /**
   * Toggle more/less options
   */
  const toggleMoreOptions = () => {
    setShowMore(!showMore);
  };

  /**
   * Export options configuration
   */
  const exportOptions = [
    { key: 'description', label: 'Description', defaultSelected: true, enabled: true },
    { key: 'approvals', label: 'Approval', defaultSelected: false, enabled: true },
    { key: 'forms', label: 'Forms', defaultSelected: false, enabled: true },
    { key: 'reviewSection', label: 'Review Section', defaultSelected: false, enabled: true },
    { key: 'attachments', label: 'Attachments', defaultSelected: false, enabled: false },
    { key: 'emails', label: 'Emails', defaultSelected: false, enabled: false },
    { key: 'activityLogs', label: 'Activity Logs', defaultSelected: false, enabled: false },
    { key: 'comments', label: 'Comments', defaultSelected: false, enabled: false },
    { key: 'linkedTasks', label: 'Linked Tasks', defaultSelected: false, enabled: false },
    { key: 'tags', label: 'Tags', defaultSelected: false, enabled: false },
    { key: 'followers', label: 'Followers', defaultSelected: false, enabled: false }
  ];

  const availableOptions = exportOptions;

  const visibleOptions = availableOptions.slice(number.ZERO, number.FOUR);
  const moreOptions = availableOptions.slice(number.FOUR);
  const displayedOptions = showMore ? availableOptions : visibleOptions;

  /**
   * Export modal content component
   */
  const exportModalContent = () => {
    return (
      <div className="export-modal-content">
        <div className="form-group mb-3">
          <label htmlFor="export-filename" className="form-label">File Name</label>
          <input
            type="text"
            id="export-filename"
            className="form-control"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            placeholder="Enter file name"
          />
        </div>

        <div className="form-group">
          <div className="d-flex align-items-center justify-content-between mb-2">
          <label className="form-label">{label.SELECT_TO_EXPORT}</label>
          <div className="d-flex align-items-center gap-2">
            <input
              type="checkbox"
              id="select-all"
              checked={exportSections.selectAll}
              onChange={() => handleSectionChange('selectAll')}
            />
            <label className="font-weight-normal mb-0" htmlFor="select-all">
              {label.SELECT_ALL}
            </label>
          </div>
          </div>

          <div className="row">
            {displayedOptions.map((option, index) => (
              <div key={option.key} className="col-6 mb-2">
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="checkbox"
                    id={option.key}
                    checked={exportSections[option.key]}
                    disabled={!option.enabled}
                    onChange={() => handleSectionChange(option.key)}
                  />
                  <label
                    className={`font-weight-normal mb-0 ${!option.enabled ? 'text-muted' : ''}`}
                    htmlFor={option.key}
                  >
                    {option.label}
                  </label>
                  {(option.key === 'forms' || option.key === 'reviewSection') && isLoadingFormData && exportPrefetchSections && (
                    <span className="ml-2 spinner-border spinner-border-sm text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {moreOptions.length > 0 && (
            <div className="mt-2 text-right">
              <span
              className="grey cursor-pointer"
                onClick={toggleMoreOptions}
              >
                {showMore ? 'less...' : 'more...'}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  /**
   * Handle export confirmation
   */
  const handleExportConfirm = () => {
    const selectedSections = Object.keys(exportSections).filter(
      key => exportSections[key] && key !== 'selectAll'
    );

    if (!fileName.trim()) {
      getNotification("Please enter a file name", notifyIcon.ERROR_ICON);
      return;
    }

    dispatch(setExportTask({
      isExporting: true,
      fileName: fileName.trim(),
      sections: selectedSections,
      taskId: task?.taskId,
      taskData: task,
      preloadedFormData: preloadedFormData // Pass preloaded form data
    }));

    // Reset states
    setShowExportModal(false);
    setExportSections({
      description: true,
      approvals: false,
      forms: false,
      reviewSection: false,
      emails: false,
      attachments: false,
      activityLogs: false,
      comments: false,
      linkedTasks: false,
      tags: false,
      followers: false,
      selectAll: false
    });
    setFileName("");
    setShowMore(false);
    setPreloadedFormData(null);
    setIsLoadingFormData(false);
  };

  /**
   * Handle export modal cancel
   */
  const handleExportCancel = () => {
    dispatch(setExportTask({ isExporting: false }));
    setShowExportModal(false);
    setPreloadedFormData(null);
    setIsLoadingFormData(false);
  };

  const handleCloseTask = async () => {
    const payload = {
      taskId: task?.taskId,
      isTaskClosed: number.ONE,
    };
    await closeTask(payload);
    closeTaskModalOpenRef.current = false;
    setShow(!show);
    setClosePopup(false);
    setShowConfirmDelete(false);
    setClonePopup(false);
    exitSidebar();
  };

  return (
    <>
      {isNewTask && (
        <div className='d-flex align-items-center justify-content-between max-width-300 create-from-label'>
          <span>Create From</span>
          <span>{`${projectCode}-xxxx`}</span>
        </div>
      )}
      <span className='header-actions'>
        <Tooltip anchorElement='target' position='bottom' parentTitle='true'>
          <button
            title={`${!isNewTask && !task.isFollowed && task.hasAccess ? "Locked" : "View"}`}
            className={` ${task.isFollowed ? "text-black border-0 outline-none" : "text-red border-0 outline-none"} ${
              isVisible ? "visible" : "invisible"
            }`}>
            {!isTrueBit(task, "InApproval") ? <span>{!isNewTask && !task.isFollowed ? icon.LOCK : icon.FOLLOWED}</span> : null}
          </button>
          {isTrueBit(task, "InApproval") && (
            <button
              id='approaval-status-icon'
              className='d-flex align-items-center justify-content-between border-0 outline-none ml-2 cursor-default'
              title={approvalStatus?.Status}>
              {icon[approvalStatus?.StatusIcon]}
            </button>
          )}
          <button className='cursor-pointer border-0 outline-none ml-2' title={task.WorkflowName} onClick={handleOpenWorkflowElements}>
            {icon[task.flowIcon]}
          </button>
          {!isNewTask && (
            <button className='ml-2 border-none' onClick={onSettings} ref={anchor}>
              {icon.SETTINGS}
            </button>
          )}
          <button className='d-flex border-0 outline-none ml-2' onClick={exitSidebar} title={label.CLOSE}>
            {icon.ARROW_FORWARD}
          </button>
        </Tooltip>
      </span>

      <Popup anchor={anchor.current} show={show} animate={true} onOpen={() => onOpen(deleteRef)} appendTo={anchor.current}>
        <div
          className='width-130'
          ref={deleteRef}
          tabIndex={number.ZERO}
          onFocus={() => onFocus(blurTimeoutRef)}
          onBlur={() => onBlur(blurTimeoutRef, hideOnBlur)}>
          <div className='popover-item d-flex flex-column'>
          <button
              id='header-actions-bookmark-task-btn'
              type='button'
              disabled={isTaskBookmarked}
              className={`btn ${isTaskBookmarked ? "btn-outline-secondary" : ""}`}
              onClick={confirmBookmarking}>
              {button.FAVORITE}
           </button>
          <button
              id='lock-task-button'
              type='button'
              onClick={toggleTaskLockPopOver}
              ref={lockPopoverAnchor}
              disabled={task?.isFollowed || task?.IsTaskComplete || task?.IsTaskClosed}
              className={`${task?.isFollowed ? "btn-outline-secondary" : ""} btn`}>
              {task?.IsTaskLocked ? (task?.LockedBy === user?.id ? button.UNLOCK_TASK : button.LOCKED) : button.LOCK_TASK}
           </button>
           <button
              id='clone-task-button'
              type='button'
              ref={cloneTaskRef}
              onClick={handleCloneTask}
            >
              {button.CLONE_TASK}
           </button>
           {showCloseButton && (
           <button
              id='header-actions-close-task-btn'
              type='button'
              ref={closeTaskRef}
              onClick={handleCloseTaskClick}
              disabled={task?.IsTaskClosed}
              className="btn">
              {task?.IsTaskClosed ? label.CLOSED : label.CLOSE}
           </button>
           )}
          <button
              id='header-actions-approval-task-btn'
              type='button'
              onClick={handleApprovalTask}
              disabled={task?.isFollowed || task?.IsTaskComplete || task?.IsTaskClosed}
              className={`${task?.isFollowed ? "btn-outline-secondary" : ""} btn`}>
              {label.APPROVAL}
           </button>
          <button
              id='delete-task-button'
              type='button'
              ref={deleteTaskRef}
              disabled={deleteDisabled}
              className={`${task?.isFollowed ? "btn-outline-secondary" : ""} `}
              onClick={deleteTask}>
              {button.DELETE_TASK}
            </button>
            <button
              id='export-task-button'
              type='button'
              ref={exportTaskRef}
              onClick={handleExportTask}
            >
              {label.EXPORT}
            </button>
          </div>
          {show && showLockPopover && (
            <TaskLockPopover
              showLockPopover={showLockPopover}
              setShowLockPopover={setShowLockPopover}
              lockPopoverAnchor={lockPopoverAnchor}
              handleLockTask={handleLockTask}
              deleteRef={deleteRef}
              setShow={setShow}
            />
          )}
          {show && showCloseButton && closePopup && (
            <CloseTaskPopup
              show={closePopup}
              anchorRef={closeTaskRef}
              onCancel={handleCloseTaskModal}
              onConfirm={handleCloseTask}
              confirmDisabled={task?.IsTaskClosed}
            />
          )}
        </div>
      </Popup>
      {
        <Popup
          anchor={cloneTaskRef.current}
          anchorAlign={{ horizontal: "left", vertical: "top" }}
          popupAlign={popupAlign("right", "top")}
          show={clonePopup}
          appendTo={cloneTaskRef.current}
          id='confirm-clone-popup'>
          <div className='popover-item-delete w-100'>
            <button onMouseDown={confirmCloning} id='confirm-clone-btn'>
              {label.CONFIRM_TEXT}
            </button>
          </div>
        </Popup>
      }
      {showConfirmDelete && show && (
        <DeletePopover
          title='confirm'
          showConfirmDelete={showConfirmDelete}
          deleteTaskRef={deleteTaskRef}
          task={task}
          setShowConfirmDelete={setShowConfirmDelete}
          setShowUndoDelete={setShowUndoDelete}
          setShow={setShow}
        />
      )}
      {showUndoDelete && <UndoNotification showUndoDelete={showUndoDelete} setShowUndoDelete={setShowUndoDelete} />}
      {showExportModal && (
        <ModalPopUp
          content={exportModalContent}
          heading="Export"
          className="export-modal-popup"
          id="task-export-popup"
          buttons={dynamicExportButtons}
          confirm={handleExportConfirm}
          hide={handleExportCancel}
          show={showExportModal}
          width="450px"
        />
      )}

      <TaskPDFExport />
    </>
  );
};

export default HeaderActions;
